/**
 * Deterministic walkthrough state for the legacy Demos walkthroughs.
 *
 * The destination state of chapter N is a pure function of
 * (scenario, N, session base time). It never depends on the path the presenter
 * took (Next, Back, jump, restart), so replaying to any chapter always produces
 * the same task, note, version, comment and review-round state with stable ids.
 * Going back is a reset to the verified baseline followed by rebuilding chapter N.
 *
 * This module only describes state. Persistence lives in demoPersistence.ts.
 */
import type { DemoAutoAction, DemoScenario, DemoStoryPoint } from '@/data/demoScenarios';

export const DEMO_STATE_ACTIONS = new Set<DemoAutoAction['type']>([
  'check-item', 'type-comment', 'add-review-note', 'add-media-version',
  'add-approver', 'approve-task', 'reject-task', 'resubmit-approval',
]);

export const DEMO_UI_ACTIONS = new Set<DemoAutoAction['type']>([
  'scroll-gantt', 'open-panel', 'close-panel', 'open-media', 'close-media',
  'show-dashboard', 'trigger-checkin', 'trigger-notification',
]);

/** Legacy actions the deterministic engine refuses (they faked progress). */
export const DEMO_UNSUPPORTED_ACTIONS = new Set<DemoAutoAction['type']>(['mark-progress', 'complete-all-tasks']);

export interface DemoItem { id: string; text: string; completed: boolean; isTimecodedNote?: boolean; timestamp?: number }
export interface DemoNote { id: string; text: string; timestamp: number; completed: boolean; createdAt: string; version: number; page?: number }
export interface DemoVersion { version: number; url: string; notes: DemoNote[]; createdAt: string; archivedAt?: string; totalPages?: number }
export interface DemoComment { id: string; text: string; authorId: string; createdAt: string }

export interface DemoTaskState {
  name: string;
  items: DemoItem[];
  notes: DemoNote[];
  versions: DemoVersion[];
  comments: DemoComment[];
  mediaUrl: string | null;
  progress: number;
}

export type DemoApprovalStatus = 'pending' | 'approved' | 'rejected';

export interface DemoApprovalRow {
  /** Global creation order within the session; drives created_at. */
  seq: number;
  approverSlot: string;
  approverName: string;
  status: DemoApprovalStatus;
  comments: string | null;
  /** Decided live by the presenter at an interactive checkpoint. */
  live?: boolean;
}

export interface DemoWorld {
  tasks: Record<string, DemoTaskState>;
  approvals: Record<string, DemoApprovalRow[]>;
}

export class DemoScriptError extends Error {
  constructor(message: string, public readonly stepIndex: number, public readonly action?: DemoAutoAction) {
    super(message);
    this.name = 'DemoScriptError';
  }
}

const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

export const demoIso = (baseMs: number, seq: number) => new Date(baseMs + seq * 60_000).toISOString();

export function baselineWorld(scenario: DemoScenario, baseMs: number): DemoWorld {
  const tasks: Record<string, DemoTaskState> = {};
  for (const t of scenario.tasks) {
    const key = `${scenario.id}:${slug(t.name)}`;
    tasks[t.name] = {
      name: t.name,
      items: (t.checklist ?? []).map((text, i) => ({ id: `${key}:item:${i}`, text, completed: false })),
      notes: [],
      versions: t.mediaUrl
        ? [{ version: 1, url: t.mediaUrl, notes: [], createdAt: demoIso(baseMs, 0), ...(t.mediaTotalPages ? { totalPages: t.mediaTotalPages } : {}) }]
        : [],
      comments: [],
      mediaUrl: t.mediaUrl ?? null,
      progress: 0,
    };
  }
  const approvals: Record<string, DemoApprovalRow[]> = {};
  for (const a of scenario.approvals) approvals[a.taskName] = [];
  return { tasks, approvals };
}

const cloneWorld = (w: DemoWorld): DemoWorld => JSON.parse(JSON.stringify(w)) as DemoWorld;

/** Latest row per approver — the current logical round. */
export function currentRound(rows: DemoApprovalRow[]): DemoApprovalRow[] {
  const latest = new Map<string, DemoApprovalRow>();
  for (const r of rows) {
    const prev = latest.get(r.approverSlot);
    if (!prev || r.seq > prev.seq) latest.set(r.approverSlot, r);
  }
  return [...latest.values()].sort((a, b) => a.seq - b.seq);
}

interface Cursor { seq: number }

function applyStateAction(
  world: DemoWorld, scenario: DemoScenario, action: DemoAutoAction, stepSlot: string,
  stepIndex: number, cursor: Cursor, baseMs: number,
) {
  const target = action.target ?? '';
  const task = world.tasks[target];
  if (!task) throw new DemoScriptError(`Unknown task "${target}"`, stepIndex, action);
  const key = `${scenario.id}:${slug(target)}`;
  const slot = action.actAsSlot || stepSlot;
  const persona = scenario.personas[slot];
  const nextSeq = () => ++cursor.seq;

  switch (action.type) {
    case 'check-item': {
      const idx = Number.parseInt(action.value ?? '', 10);
      const item = task.items[idx];
      if (!item) throw new DemoScriptError(`"${target}" has no action item ${action.value}`, stepIndex, action);
      item.completed = true;
      if (item.isTimecodedNote) {
        for (const n of task.notes) if (n.id === item.id) n.completed = true;
        for (const v of task.versions) for (const n of v.notes) if (n.id === item.id) n.completed = true;
      }
      return;
    }
    case 'type-comment': {
      if (!action.value) throw new DemoScriptError('Comment text missing', stepIndex, action);
      const seq = nextSeq();
      task.comments.push({ id: `${key}:comment:${seq}`, text: action.value, authorId: slot, createdAt: demoIso(baseMs, seq) });
      return;
    }
    case 'add-review-note': {
      if (!action.value) throw new DemoScriptError('Note text missing', stepIndex, action);
      const active = task.versions.find(v => !v.archivedAt);
      if (!active) throw new DemoScriptError(`"${target}" has no media to annotate`, stepIndex, action);
      const pageMatch = action.value.match(/\s*\[p\.(\d+)\]\s*$/);
      const text = action.value.replace(/\s*\[p\.\d+\]\s*$/, '');
      const seq = nextSeq();
      const id = `${key}:note:${seq}`;
      const timestamp = action.timestamp ?? 0;
      const note: DemoNote = {
        id, text, timestamp, completed: false, createdAt: demoIso(baseMs, seq), version: active.version,
        ...(pageMatch ? { page: Number(pageMatch[1]) } : {}),
      };
      task.notes.push(note);
      active.notes.push({ ...note });
      task.items.push({ id, text, completed: false, isTimecodedNote: true, timestamp });
      return;
    }
    case 'add-media-version': {
      if (!action.value) throw new DemoScriptError('Media URL missing', stepIndex, action);
      const seq = nextSeq();
      const at = demoIso(baseMs, seq);
      for (const v of task.versions) if (!v.archivedAt) v.archivedAt = at;
      const next = task.versions.reduce((m, v) => Math.max(m, v.version), 0) + 1;
      task.versions.push({ version: next, url: action.value, notes: [], createdAt: at });
      task.mediaUrl = action.value;
      return;
    }
    case 'add-approver': {
      const config = scenario.approvals.find(a => a.taskName === target);
      if (!config) throw new DemoScriptError(`"${target}" has no configured reviewers`, stepIndex, action);
      const rows = world.approvals[target];
      const round = currentRound(rows);
      for (const approverSlot of config.approverSlots) {
        if (round.some(r => r.approverSlot === approverSlot)) continue;
        const p = scenario.personas[approverSlot];
        if (!p) throw new DemoScriptError(`Unknown reviewer slot ${approverSlot}`, stepIndex, action);
        rows.push({ seq: nextSeq(), approverSlot, approverName: p.name, status: 'pending', comments: null });
      }
      return;
    }
    case 'approve-task':
    case 'reject-task': {
      if (!persona) throw new DemoScriptError(`Unknown persona slot ${slot}`, stepIndex, action);
      const rows = world.approvals[target];
      if (!rows) throw new DemoScriptError(`"${target}" has no reviews`, stepIndex, action);
      const mine = currentRound(rows).find(r => r.approverSlot === slot);
      if (!mine || mine.status !== 'pending') {
        throw new DemoScriptError(`${persona.name} has no open review on "${target}"`, stepIndex, action);
      }
      if (action.type === 'approve-task') {
        mine.status = 'approved';
        mine.comments = action.value ?? null;
      } else {
        const feedback = action.value || 'Changes needed';
        mine.status = 'rejected';
        mine.comments = feedback;
        const n = task.items.filter(i => i.text.startsWith('[Revision]')).length;
        task.items.push({ id: `${key}:revision:${n}`, text: `[Revision] ${feedback}`, completed: false });
      }
      return;
    }
    case 'resubmit-approval': {
      const rows = world.approvals[target];
      if (!rows) throw new DemoScriptError(`"${target}" has no reviews`, stepIndex, action);
      const rejected = currentRound(rows).filter(r => r.status === 'rejected');
      if (!rejected.length) throw new DemoScriptError(`"${target}" has no rejected review to resubmit`, stepIndex, action);
      if (task.items.some(i => !i.completed)) throw new DemoScriptError(`"${target}" cannot be resubmitted with open items`, stepIndex, action);
      for (const r of rejected) {
        rows.push({ seq: nextSeq(), approverSlot: r.approverSlot, approverName: r.approverName, status: 'pending', comments: null });
      }
      return;
    }
    default:
      return;
  }
}

function finalizeProgress(world: DemoWorld, scenario: DemoScenario, stepIndex: number) {
  const byName = new Map(scenario.tasks.map(t => [t.name, t]));
  const children = new Map<string, string[]>();
  for (const t of scenario.tasks) {
    if (t.parentName) children.set(t.parentName, [...(children.get(t.parentName) ?? []), t.name]);
  }
  const computed = new Map<string, number>();
  const compute = (name: string): number => {
    if (computed.has(name)) return computed.get(name)!;
    const kids = children.get(name);
    const state = world.tasks[name];
    let progress: number;
    if (kids?.length) {
      const vals = kids.map(k => ({ p: compute(k), w: byName.get(k)?.durationHours || 1 }));
      const allDone = vals.every(v => v.p === 100);
      const weighted = Math.round(vals.reduce((s, v) => s + v.p * v.w, 0) / vals.reduce((s, v) => s + v.w, 0));
      progress = allDone ? 100 : Math.min(weighted, 99);
    } else {
      const total = state.items.length;
      const done = state.items.filter(i => i.completed).length;
      progress = total ? Math.round((done / total) * 100) : 0;
      const rows = world.approvals[name];
      if (rows && progress === 100) {
        const round = currentRound(rows);
        if (!(round.length > 0 && round.every(r => r.status === 'approved'))) progress = 90;
      }
    }
    computed.set(name, progress);
    return progress;
  };
  for (const t of scenario.tasks) world.tasks[t.name].progress = compute(t.name);

  // Canonical gate parity: nothing reaches 100% before its FS predecessor.
  for (const d of scenario.dependencies) {
    if (d.type !== 'finish-to-start') continue;
    if (world.tasks[d.to]?.progress === 100 && world.tasks[d.from]?.progress !== 100) {
      throw new DemoScriptError(`"${d.to}" completes before its predecessor "${d.from}"`, stepIndex);
    }
  }
}

export const stateActionsOf = (step: DemoStoryPoint) => (step.autoActions ?? []).filter(a => DEMO_STATE_ACTIONS.has(a.type));
export const uiActionsOf = (step: DemoStoryPoint) => (step.autoActions ?? []).filter(a => DEMO_UI_ACTIONS.has(a.type));

/** True when this action is the decision an interactive checkpoint leaves to the presenter. */
export function isCheckpointDecision(step: DemoStoryPoint, action: DemoAutoAction): boolean {
  const cp = step.checkpoint;
  if (!cp) return false;
  return (action.type === 'approve-task' || action.type === 'reject-task')
    && action.target === cp.target && (action.actAsSlot || step.actAsSlot) === cp.approverSlot;
}

export interface WorldOptions {
  /** Build chapter `index` without its checkpoint decision (state before the presenter decides). */
  preDecision?: boolean;
  /** Chapters whose checkpoint decision was confirmed live this session. */
  liveCheckpoints?: ReadonlySet<number>;
}

/** Pure destination state for chapter `index` (inclusive). */
export function worldAtStep(scenario: DemoScenario, index: number, baseMs: number, opts: WorldOptions = {}): DemoWorld {
  const world = cloneWorld(baselineWorld(scenario, baseMs));
  const cursor: Cursor = { seq: 0 };
  const last = Math.max(-1, Math.min(index, scenario.storyPoints.length - 1));
  for (let i = 0; i <= last; i++) {
    const step = scenario.storyPoints[i];
    for (const action of step.autoActions ?? []) {
      if (DEMO_UNSUPPORTED_ACTIONS.has(action.type)) {
        throw new DemoScriptError(`"${action.type}" is not supported — use real action items and reviews`, i, action);
      }
      if (!DEMO_STATE_ACTIONS.has(action.type)) continue;
      const decision = isCheckpointDecision(step, action);
      if (decision && opts.preDecision && i === last) continue;
      applyStateAction(world, scenario, action, step.actAsSlot, i, cursor, baseMs);
      if (decision && opts.liveCheckpoints?.has(i)) {
        const rows = world.approvals[action.target ?? ''];
        const mine = rows && currentRound(rows).find(r => r.approverSlot === step.checkpoint!.approverSlot);
        if (mine) mine.live = true;
      }
    }
    finalizeProgress(world, scenario, i);
  }
  if (last < 0) finalizeProgress(world, scenario, 0);
  return world;
}

/** Validate every chapter of a script builds. Returns problems; never throws. */
export function validateScenarioScript(scenario: DemoScenario): string[] {
  const problems: string[] = [];
  try {
    worldAtStep(scenario, scenario.storyPoints.length - 1, 0);
  } catch (err) {
    const e = err as DemoScriptError;
    problems.push(`chapter ${(e.stepIndex ?? -1) + 1}: ${e.message}`);
  }
  for (const [i, step] of scenario.storyPoints.entries()) {
    if (!scenario.personas[step.actAsSlot]) problems.push(`chapter ${i + 1}: unknown persona ${step.actAsSlot}`);
    for (const a of step.autoActions ?? []) {
      if (a.actAsSlot && !scenario.personas[a.actAsSlot]) problems.push(`chapter ${i + 1}: unknown persona ${a.actAsSlot}`);
      if (a.target && !['show-dashboard', 'trigger-notification'].includes(a.type) && !scenario.tasks.some(t => t.name === a.target)) {
        problems.push(`chapter ${i + 1}: unknown task ${a.target}`);
      }
    }
  }
  return problems;
}

/** Task names whose persisted state differs between two worlds. */
export function changedTasks(prev: DemoWorld | null, next: DemoWorld): string[] {
  return Object.keys(next.tasks).filter(name => {
    if (!prev) return true;
    return JSON.stringify(prev.tasks[name]) !== JSON.stringify(next.tasks[name])
      || JSON.stringify(prev.approvals[name] ?? []) !== JSON.stringify(next.approvals[name] ?? []);
  });
}
